import { Button, Card, ConfirmModal, Modal, Panel } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { getCityCode, getConcatCity } from '@/components/utils';
import { LEVEL_TYPE, Level, PAGE_TYPE, ROLE_TYPE } from '@/constants';
import usePageType from '@/hooks/usePageType';
import useRouter from '@/hooks/useRouter';
import { useReadyInfo } from '@/providers/ReadyProvider';
import {
  fetchAddeEditMember,
  fetchGePcsList,
  fetchMemberInfo,
  fetchPhoneVerify,
} from '@/services/account';
import { getParamsWithNilToString } from '@/services/utils';
import { replace } from '@/utils/history';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Alert, IconButton } from 'rsuite';
import ExchangeRecord from './components/ExchangeRecord';
import MbrPromotion from './components/MbrPromotion';
import ModifyLog from './components/ModifyLog';
import ReceiptRecord from './components/ReceiptRecord';
import ServiceRecord from './components/ServiceRecord';
import * as Yup from 'yup';
import { Form } from '@/components/formik-rsuite';
import { isAfter } from 'date-fns';
import { useModal } from '@ebay/nice-modal-react';
import PageLoader from '@/components/shared/PageLoader';
import useLoading from '@/hooks/useLoading';
import RSVPRecord from './components/RSVPRecord';
import MbrCeremonyModal from './components/Modal/MbrCeremonyModal';
import HelpO from '@rsuite/icons/legacy/HelpO';
import { FORM_DEFAULT_DISPLAY } from '@/constants/config';
import LastLevelInfoModal from './components/Modal/LastLevelInfoModal';
import options from '@/utils/options';
import useBlackBookMemberList from '@/hooks/preload/useBlackBookMemberList';

interface MemberInfoForm {
  name: string; // 客户名
  other_name: string; // 客人别称
  sex: string; // 性别
  phone: string; // 手机号
  birthday: string; // 出生日期
  address: string; // 地址
  email: string; // 邮箱
  city: string; // 城市
  job: string; // 职业
  marry_time: string; // 结婚纪念日
  family: string; // 家庭状况
  car_number: string; // 常用车牌号
  is_hotboom: string; // 是否为代购
  is_has_pet: string; // 养宠状况
  is_staff: string; // 是否为员工
  other_like_brand: string; // 其他品牌喜好
  gift_demand: string; // 送礼需求
  hooby: string; // 业余爱好
  like_food: string; // 食物喜好
  other_like: string; // 其他喜好
  other_shooping_village: string; // 去过其他购物村
  need_other_shopping_village: string; // 有去其他购物村的需求
  social_media_id: string; // 社交媒体ID
  remark: string; // 备注
  black_book_member: string; // 黑书会员
  black_book_member_content: string; // 黑书会员渠道名称
  ge_id: string | number; // 所属购物村大使id
  pcs_id: string | number; // 所属导购大使id
  main_contacts_id: string | number; // 主联系人 1购物村大使 2导购大使
  yizun_membership_ceremony: string | number; // 奕尊入会礼状态 1未领取（为1时显示代客领取按钮） 2已领取未兑换 3已兑换
  mall_level: string; // 正价商场会员等级
}

const Member = () => {
  const pageType = usePageType();
  const { data: blackBookMemberList, loading: blackBookMemberListLoading } =
    useBlackBookMemberList();
  const modal = useModal(ConfirmModal);
  const mbrCeremonyModal = useModal(MbrCeremonyModal);
  const lastLevelInfoModal = useModal(LastLevelInfoModal);
  const { profile, allCities } = useReadyInfo();
  const { user_name, shopping_village } = profile;
  // 出生日期判断 不允许选择14岁以内的日期
  const birthdayMinDate = new Date(
    new Date().getFullYear() - 14,
    new Date().getMonth(),
    new Date().getDate()
  );

  const { match, query } = useRouter<{ id: string }>();

  const isCreate = pageType === PAGE_TYPE.CREATE;
  const [isEdit, setIsEdit] = useState(false);

  const defaultMemberInfo: MemberInfoForm = {
    name: '',
    other_name: '',
    sex: '',
    phone: '',
    birthday: '',
    address: '',
    email: '',
    city: '',
    job: '',
    marry_time: '',
    family: '',
    car_number: '',
    is_hotboom: '',
    is_has_pet: '',
    is_staff: '',
    other_like_brand: '',
    gift_demand: '',
    hooby: '',
    like_food: '',
    other_like: '',
    other_shooping_village: '',
    need_other_shopping_village: '',
    social_media_id: '',
    remark: '',
    black_book_member: '',
    black_book_member_content: '',
    ge_id: '',
    pcs_id: '',
    main_contacts_id: '',
    yizun_membership_ceremony: '',
    mall_level: '',
  };

  const [defaultInfo, setDefaultInfo] = useState<MemberInfoForm>(defaultMemberInfo);

  const [saveInfoModal, setSaveInfoModal] = useState({
    show: false,
    content: '',
    confirm: '',
    cancel: '',
    onConfirm: null,
  });

  // 获取大使列表
  const {
    run: getGePcsList,
    data: gePcsList,
    loading: gePcsListLoading,
  } = useRequest(fetchGePcsList, {
    manual: true,
  });

  const {
    run: getMemberInfo,
    data: memberInfoData,
    loading: memberInfoLoading,
  } = useRequest(fetchMemberInfo, {
    manual: true,
  });

  const { run: addeOrEditMember, loading: addeOrEditMemberLoading } = useRequest(
    fetchAddeEditMember,
    {
      manual: true,
    }
  );

  const formik = useFormik({
    initialValues: defaultInfo,
    enableReinitialize: true, // initialValues默认值受控
    validateOnChange: false,
    validationSchema: Yup.object({
      name: Yup.string().required('请输入客户名'),
      phone: Yup.string()
        .required('请输入手机号')
        .test((value, context) => {
          return fetchPhoneVerify({
            type: 1,
            phone: value,
            ...(!isCreate && { id: match.params.id }),
          }).then((res) => {
            if (res?.is_verify) {
              return true;
            }
            return context.createError({ message: res?.verify_msg });
          });
        }),
      email: Yup.string().email('请输入正确的邮箱'),
    }),
    onSubmit(values, actions) {
      if (formik.isValid) {
        setSaveInfoModal({
          show: true,
          content: '是否保存本次编辑？',
          confirm: '保存',
          cancel: '暂不',
          onConfirm: () => handleSave(values),
        });
      }
    },
  });

  const fetchInfo = (memberId?: string | number) => {
    getMemberInfo(memberId || match.params.id).then((res) => {
      const {
        name,
        other_name,
        sex,
        phone,
        birthday,
        address,
        email,
        city,
        job,
        marry_time,
        family,
        car_number,
        is_hotboom,
        is_has_pet,
        is_staff,
        other_like_brand,
        gift_demand,
        hooby,
        like_food,
        other_like,
        other_shooping_village,
        need_other_shopping_village,
        social_media_id,
        remark,
        black_book_member,
        black_book_member_content,
        ge_id,
        pcs_id,
        main_contacts_id,
        yizun_membership_ceremony,
        mall_level,
      } = res;
      const defaultValues = {
        name,
        other_name,
        sex,
        phone,
        birthday,
        address,
        email,
        city: getCityCode(city, allCities),
        job,
        marry_time,
        family,
        car_number,
        is_hotboom,
        is_has_pet,
        is_staff,
        other_like_brand,
        gift_demand,
        hooby,
        like_food,
        other_like,
        other_shooping_village,
        need_other_shopping_village,
        social_media_id,
        remark,
        black_book_member,
        black_book_member_content,
        ge_id,
        pcs_id,
        main_contacts_id,
        yizun_membership_ceremony,
        mall_level,
      };
      formik.setValues(defaultValues);
      setDefaultInfo(defaultValues);
    });
  };

  const handleSave = (values) => {
    const newValues = Object.assign({}, values, {
      city: getConcatCity(values.city, allCities),
    });
    const params = getParamsWithNilToString(newValues);
    addeOrEditMember({
      data: {
        ...params,
        ...(isEdit && { id: match.params.id }),
        black_book_member_content: params?.black_book_member ? params?.black_book_member_content : '',
      },
    }).then((res) => {
      if (res?.id) {
        const { id } = res;
        setIsEdit(false);
        Alert.success('保存成功');
        // 创建成功后跳到详情页，修改成功停留在本页且重新获取用户信息
        if (isCreate) {
          formik.resetForm(); // 重置后不会被guard拦截
          replace(`/customer-account/member/detail/${id}`);
        }
        fetchInfo(id);
      }
    });
    handleHideModal();
  };

  useEffect(() => {
    if (pageType === PAGE_TYPE.CREATE) {
      // 重置表单
      formik.resetForm();
    }
  }, [pageType]);

  useEffect(() => {
    if (pageType === PAGE_TYPE.DETAIL && allCities) {
      fetchInfo();
    }
  }, [allCities]);

  useEffect(() => {
    getGePcsList();
  }, []);

  const handleHideModal = () => {
    setSaveInfoModal({
      ...saveInfoModal,
      show: false,
    });
  };

  const loading = useLoading(
    gePcsListLoading || blackBookMemberListLoading || memberInfoLoading || addeOrEditMemberLoading
  );

  return (
    <>
      {loading && <PageLoader />}
      <FormikProvider value={formik}>
        <Form guard>
          <Panel
            title="客户信息"
            sideBarBtn={
              !isCreate &&
              [ROLE_TYPE.MANAGER, ROLE_TYPE.GANGER, ROLE_TYPE.AMBASSADOR, ROLE_TYPE.DRT].includes(
                profile?.role
              )
            } // 创建页没有快捷服务导航，GE经理、GE领班、GE迎宾大使、DRT导购大使才有快捷服务导航
            toolbar={
              <div className="flex justify-between w-fit">
                <IconButton
                  className="flex items-center mr-[30px]"
                  disabled={isCreate}
                  onClick={() => {
                    if (isEdit) {
                      if (formik.dirty) {
                        modal.show({
                          contentText: '信息还没保存，确定离开吗？',
                          submitText: '确定',
                          cancelText: '取消',
                          onSubmit: () => {
                            formik.resetForm();
                            setIsEdit(!isEdit);
                          },
                        });
                      } else {
                        formik.resetForm();
                        setIsEdit(!isEdit);
                      }
                    } else {
                      setIsEdit(!isEdit);
                    }
                  }}
                >
                  <Image src="/resources/images/edit.svg" />
                  <div className="text-[12px] text-[#999999] ml-[5px]">
                    {isEdit ? '取消' : '编辑'}
                  </div>
                </IconButton>
                <IconButton
                  type="submit"
                  className="flex items-center"
                  disabled={!isCreate && !isEdit}
                  onClick={() => {
                    formik.submitForm();
                  }}
                >
                  <Image src="/resources/images/save.svg" />
                  <div className="text-[12px] text-[#999999] ml-[5px]">保存</div>
                </IconButton>
              </div>
            }
          >
            <div className="flex">
              <div style={{ width: 'calc(33.33% - 51.33px)' }}>
                <div className="flex flex-col">
                  <Card title="个人信息">
                    <Card.Input
                      isEdit={isCreate || isEdit}
                      required
                      label="客户名"
                      formKey="name"
                    />
                    <Card.Input isEdit={isCreate || isEdit} label="客人别称" formKey="other_name" />
                    <Card.Picker
                      isEdit={isCreate || isEdit}
                      label="性别"
                      formKey="sex"
                      pickerType="sex"
                    />
                    <Card.Input
                      isEdit={isCreate || isEdit}
                      required
                      label="手机号"
                      formKey="phone"
                    />
                    <Card.DatePicker
                      isEdit={isCreate || isEdit}
                      label="出生日期"
                      formKey="birthday"
                      disabledDate={(date) => isAfter(date, birthdayMinDate)}
                      calendarDefaultDate={birthdayMinDate}
                    />
                    <Card.Input isEdit={isCreate || isEdit} label="地址" formKey="address" />
                    <Card.Input isEdit={isCreate || isEdit} label="邮箱" formKey="email" />
                    <Card.CityPicker
                      isEdit={isCreate || isEdit}
                      label="城市"
                      formKey="city"
                      value={memberInfoData?.city}
                    />
                    <Card.Input isEdit={isCreate || isEdit} label="职业" formKey="job" />
                  </Card>
                  <Card title="消费概况">
                    <Card.Input
                      notEditable
                      label="今年消费总计"
                      formKey="price_this_year"
                      inputType="price"
                      value={isCreate ? '' : memberInfoData?.price_this_year}
                    />
                    <Card.Input
                      notEditable
                      label="今年到村总次数(上海/苏州)"
                      formKey="count_this_year"
                      value={
                        isCreate
                          ? ''
                          : `${memberInfoData?.count_this_year_sh}次/${memberInfoData?.count_this_year_sz}次`
                      }
                      {...(!isCreate && { defaultValue: '0次/0次' })}
                    />
                    <Card.Input
                      notEditable
                      label="上年度消费总计"
                      formKey="price_last_year"
                      inputType="price"
                      value={isCreate ? '' : memberInfoData?.price_last_year}
                    />
                    <Card.Input
                      notEditable
                      label="上年度到村次数"
                      formKey="count_last_year"
                      value={isCreate ? '' : `${memberInfoData?.count_last_year}次`}
                      {...(!isCreate && { defaultValue: 0 })}
                    />
                    <Card.Input
                      notEditable
                      label="消费总计"
                      formKey="price_year"
                      inputType="price"
                      value={isCreate ? '' : memberInfoData?.price_year}
                    />
                  </Card>
                </div>
              </div>
              <div className="w-[77px]"></div>
              <div style={{ width: 'calc(33.33% - 51.33px)' }}>
                <Card title="个性化备注">
                  <Card.Input
                    isEdit={isCreate || isEdit}
                    label="结婚纪念日"
                    placeholder="请输入月/日"
                    formKey="marry_time"
                  />
                  <Card.Input isEdit={isCreate || isEdit} label="家庭状况" formKey="family" />
                  <Card.Input isEdit={isCreate || isEdit} label="常用车牌号" formKey="car_number" />
                  <Card.Picker
                    isEdit={isCreate || isEdit}
                    label="是否为代购"
                    formKey="is_hotboom"
                    pickerType="TorFWithStr"
                  />
                  <Card.Input isEdit={isCreate || isEdit} label="养宠状况" formKey="is_has_pet" />
                  <Card.Picker
                    isEdit={isCreate || isEdit}
                    label="是否为员工"
                    formKey="is_staff"
                    pickerType="TorFWithStr"
                  />
                  <Card.Input
                    isEdit={isCreate || isEdit}
                    label="其他品牌喜好"
                    formKey="other_like_brand"
                  />
                  <Card.Input isEdit={isCreate || isEdit} label="送礼需求" formKey="gift_demand" />
                  <Card.Input isEdit={isCreate || isEdit} label="业余爱好" formKey="hooby" />
                  <Card.Input isEdit={isCreate || isEdit} label="食物喜好" formKey="like_food" />
                  <Card.Input isEdit={isCreate || isEdit} label="其他喜好" formKey="other_like" />
                  <Card.Input
                    isEdit={isCreate || isEdit}
                    label="去过其他购物村"
                    formKey="other_shooping_village"
                  />
                  <Card.Input
                    isEdit={isCreate || isEdit}
                    label="有去其他购物村的需求"
                    formKey="need_other_shopping_village"
                  />
                  <Card.Input
                    isEdit={isCreate || isEdit}
                    label="如是博主：社交媒体ID"
                    formKey="social_media_id"
                  />
                  <Card.Input
                    isEdit={isCreate || isEdit}
                    label="备注"
                    formKey="remark"
                    inputType="textarea"
                  />
                </Card>
              </div>
              <div className="w-[77px]"></div>
              <div style={{ width: 'calc(33.33% - 51.33px)' }}>
                <div className="flex flex-col">
                  <Card title="会籍信息">
                    <Card.Input
                      notEditable
                      label="当前会员等级"
                      formKey="level"
                      inputType="level"
                      value={
                        (
                          <div className="flex items-center">
                            <div className="mr-[5px]">
                              {Level?.[isCreate ? '' : memberInfoData?.level] ||
                                FORM_DEFAULT_DISPLAY}
                            </div>
                            {!isCreate && (
                              <HelpO
                                className="text-base"
                                onClick={() =>
                                  lastLevelInfoModal.show({
                                    memberId: Number(match.params.id),
                                  })
                                }
                              />
                            )}
                          </div>
                        ) as any
                      }
                    />
                    <Card.Input
                      notEditable
                      label="会员号"
                      formKey="user_code"
                      value={isCreate ? '' : memberInfoData?.user_code}
                    />
                    <Card.DatePicker
                      notEditable
                      label="会员注册时间"
                      formKey="create_at"
                      value={isCreate ? '' : memberInfoData?.create_at}
                    />
                    <Card.DatePicker
                      notEditable
                      label="会员到期时间"
                      formKey="valid_level_time"
                      value={isCreate ? '' : memberInfoData?.valid_level_time}
                    />
                    <Card.Input
                      notEditable
                      label="注册购物村"
                      formKey="village"
                      inputType="village"
                      value={isCreate ? shopping_village : memberInfoData?.shopping_village}
                    />
                    <Card.Picker
                      isEdit={isCreate || isEdit}
                      label="黑书会员"
                      formKey="black_book_member"
                      pickerData={blackBookMemberList?.reason_list}
                      onChange={(val) => {
                        formik.setFieldValue('black_book_member', val);
                        val !== formik.values?.black_book_member && formik.setFieldValue('black_book_member_content', '');
                      }}
                    />
                    {formik.values?.black_book_member && (
                      <Card.Input
                        isEdit={isCreate || isEdit}
                        label=" "
                        placeholder={
                          blackBookMemberList?.reason_list?.find(
                            (item) => item.value === formik.values?.black_book_member
                          )?.placeholder
                        }
                        formKey="black_book_member_content"
                      />
                    )}
                    <Card.Input
                      notEditable
                      label="注册邀请人"
                      formKey="recommend_name"
                      value={isCreate ? '' : memberInfoData?.recommend_name}
                    />
                    <Card.Input
                      notEditable
                      label="邀请人购物村"
                      formKey="recommend_shopping_village"
                      inputType="village"
                      value={isCreate ? '' : memberInfoData?.recommend_shopping_village}
                    />
                    <Card.Input
                      notEditable
                      label="会籍创建人"
                      formKey="creator"
                      value={isCreate ? user_name : memberInfoData?.creator}
                    />
                    {memberInfoData?.level === LEVEL_TYPE.YIZUN && (
                      <Card.Input
                        notEditable
                        label="奕尊入会礼状态"
                        formKey="yizun_membership_ceremony_id"
                        value={options<any, any>(
                          {
                            // 1: () => (
                            //   <Button
                            //     appearance="link"
                            //     className="p-0"
                            //     onClick={() =>
                            //       mbrCeremonyModal.show({
                            //         userId: Number(match.params.id),
                            //         onReGetInfo: fetchInfo,
                            //       })
                            //     }
                            //   >
                            //     未领取
                            //   </Button>
                            // ),
                            1: () => '未领取',
                            2: () => '已领取未兑换',
                            3: () => '已兑换',
                          },
                          memberInfoData?.yizun_membership_ceremony
                        )}
                      />
                    )}
                    <Card.Input
                      isEdit={isCreate || isEdit}
                      label="正价商场会员等级"
                      formKey="mall_level"
                      value={isCreate ? user_name : memberInfoData?.mall_level}
                    />
                  </Card>
                  <Card title="大使信息">
                    <Card.PickerWithCheck
                      isEdit={isCreate || isEdit}
                      label="所属购物村大使"
                      formKey="ge_id"
                      checkKey="main_contacts_id"
                      checkLabel="主联系人"
                      currentLabel={memberInfoData?.ge_name}
                      pickerData={
                        gePcsList?.ge_list?.map((ge) => {
                          return {
                            label: ge.realname,
                            value: JSON.stringify(ge.id),
                          };
                        }) ?? []
                      }
                    />
                    <Card.PickerWithCheck
                      isEdit={isCreate || isEdit}
                      label="所属导购大使"
                      formKey="pcs_id"
                      checkKey="main_contacts_id"
                      checkLabel="主联系人"
                      currentLabel={memberInfoData?.pcs_name}
                      pickerData={
                        gePcsList?.pcs_list?.map((pcs) => {
                          return {
                            label: pcs.realname,
                            value: JSON.stringify(pcs.id),
                          };
                        }) ?? []
                      }
                    />
                  </Card>
                </div>
              </div>
            </div>
          </Panel>
        </Form>
      </FormikProvider>
      {!isCreate && (
        <>
          {[ROLE_TYPE.MANAGER, ROLE_TYPE.DRT].includes(profile.role) && ( // GE经理和DRT导购大使有会员升降级和邀请成为奕尚
            <MbrPromotion
              isEdit={!isEdit}
              data={{
                level: memberInfoData?.level,
                valid_level_time: memberInfoData?.valid_level_time,
              }}
              saList={
                gePcsList?.sa_list?.map((sa) => {
                  return {
                    label: sa.realname,
                    value: sa.id,
                  };
                }) ?? []
              }
              onReGetInfo={fetchInfo}
            />
          )}
          <ReceiptRecord data={{ name: formik.values.name }} />
          <ServiceRecord />
          <ExchangeRecord />
          <RSVPRecord />
          <ModifyLog />
        </>
      )}
      <Modal
        show={saveInfoModal.show}
        content={saveInfoModal.content}
        confirm={saveInfoModal.confirm}
        cancel={saveInfoModal.cancel}
        onConfirm={saveInfoModal.onConfirm}
        onCancel={handleHideModal}
      />
    </>
  );
};

export default Member;
